import React, { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { gql } from 'apollo-boost';
import { useLazyQuery } from '@apollo/react-hooks';
import { FaPlay, FaPause, FaAngleDown } from 'react-icons/fa';
import { FaArrowRight, FaChevronDown } from 'react-icons/fa';
import { addHours, getMinutes, getHours, getSeconds } from 'date-fns';
import { AccordionItem, Accordion, AccordionButton, AccordionPanel } from '@reach/accordion';
import Loading from '../../../components/Loading';
import { sorts } from '../../../utils';
import PlayPauseButton from '../../../components/PlayPauseButton';

const AUDIOENGINE_PLAYLIST = gql`
    query ($contentId: String!) {
        audioEnginePlaylist(contentId: $contentId) {
            expires
            playlist_token
            playlist {
                chapter_number
                part_number
                url
            }
        }
    }
`;

const GET_AUDIOCOHORT = gql`
    query AudioCohort($contentId: String!) {
        audioCohort(contentId: $contentId) {
            versionId
            fileList {
                checksum
                fileName
                url
            }
            sampleList {
                checksum
                fileName
                url
            }
            playList {
                sectionType
                sectionName
                aePartNumber
                segments {
                    name
                    aeChapterNumber
                    start {
                        checksum
                        offset
                    }
                    end {
                        checksum
                        offset
                    }
                }
            }
        }
    }
`;

export const convertToDuration = (secondsAmount) => {
    const normalizeTime = (time) => (time.length === 1 ? `0${time}` : time);

    const SECONDS_TO_MILLISECONDS_COEFF = 1000;
    const MINUTES_IN_HOUR = 60;

    const milliseconds = secondsAmount * SECONDS_TO_MILLISECONDS_COEFF;

    const date = new Date(milliseconds);
    const timezoneDiff = date.getTimezoneOffset() / MINUTES_IN_HOUR;
    const dateWithoutTimezoneDiff = addHours(date, timezoneDiff);

    const hours = normalizeTime(String(getHours(dateWithoutTimezoneDiff)));
    const minutes = normalizeTime(String(getMinutes(dateWithoutTimezoneDiff)));
    const seconds = normalizeTime(String(getSeconds(dateWithoutTimezoneDiff)));

    return `${hours}:${minutes}:${seconds}`;
};

const getChecksumAndUrl = (startChecksum = null, endChecksum = null, fileList) => {
    let list = [...new Set([startChecksum, endChecksum])].filter((item) => item);
    //filtering as a precaution in case it wasnt passed in and is null.

    if (list && list.length > 1) {
        let checksums = fileList.map((f) => f.checksum);
        // need to compile a list of files to play.
        let firstFileIndex = checksums.findIndex((el) => el === list[0]);
        let lastFileIndex = checksums.findIndex((el) => el === list[1]);
        //all checksums should be in the correct play order (there is a rare chance that a AS file can contain 3 AE files in total to play - but we will only have start checksum and end checksum) and since the order should never change we add the missing index to the indexes with the following logic so we go through checksums to find all urls to play.
        let indexes = [firstFileIndex, lastFileIndex].reduce(function (acc, cur, ind, arr) {
            let diff = 0;
            if (ind !== 0) {
                diff = cur - arr[ind - 1];
            }
            if (diff > 1) {
                let i = 1;
                while (i < diff) {
                    acc.push(arr[ind - 1] + i);
                    i++;
                }
            }
            return acc;
        }, []);
        indexes = [firstFileIndex, lastFileIndex]
            .concat(indexes)
            .sort((x, y) => sorts.asc(x, y))
            .filter((val) => val !== -1);
        list = indexes.map((n) => {
            //if -1 then could not find the checksum (this is a rare chance there is bad data - used as a precaution to avoid page error)
            if (n !== -1) {
                return { checksum: fileList[n].checksum, url: fileList[n].url };
            }
            return null;
        });
    }
    if (list && list.length === 1) {
        let checksums = fileList.map((f) => f.checksum);
        let fileIndex = checksums.findIndex((el) => el === list[0]);
        if (fileIndex !== -1) {
            list = [{ checksum: fileList[fileIndex]?.checksum, url: fileList[fileIndex]?.url }];
        }
    }
    return list;
};

const Audio = ({ contentId, chapterized = false, sampleUrl }) => {
    const [audioEnginePlaylist, { data, loading, error: aePlaylistError }] = useLazyQuery(AUDIOENGINE_PLAYLIST);
    const [getAudioCohort, { loading: loadingAudioCohort, error, data: audioCohortData }] =
        useLazyQuery(GET_AUDIOCOHORT);
    const [currentTrack, setCurrentTrack] = useState(null);
    const audio = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const [currentAudioServiceId, setCurrentAudioServiceId] = useState(null);

    const handlePlayTrack = (track) => {
        currentAudioServiceId && setCurrentAudioServiceId(null);
        setIsPlaying(true);
        setCurrentTrack(track.url);

        audio.current.onloadedmetadata = function handlePlay(event) {
            audio.current.pause();
            event.target.currentTime = 0;
            event.target.play();
        };
    };

    const handlePauseTrack = () => {
        audio.current.pause();
        setIsPlaying(false);
    };

    const handlePlayAudioServiceTrack = (track, offset = { start: 0, end: null }, id = null) => {
        setIsPlaying(true);
        setCurrentTrack(track.url);
        id && setCurrentAudioServiceId(id);
        if (currentTrack === track.url) {
            audio.current.currentTime = offset.start + 1;
            audio.current.play();
        }
        /*When  an offset has been set and there is an audio ref once data is loaded set the currentTime and play()*/
        if (offset.start !== null && offset.start >= 0 && audio?.current) {
            audio.current.onloadedmetadata = function handlePlay(event) {
                event.target.currentTime = offset.start;
                event.target.play();
                if (offset?.end !== null && offset?.end > 0 && audio?.current) {
                    audio.current.ontimeupdate = function handleTimeUpdate(event) {
                        Math.round(event.target.currentTime) === Math.round(offset?.end) && audio.current.pause();
                    };
                }
                if (offset?.end === null && audio?.current) {
                    audio.current.ontimeupdate = null;
                }
            };
        }
    };

    const toggleTrack = (item, offset = { start: 0, end: null }, id = null) => {
        if (currentTrack !== item?.url) {
            if (id) handlePlayAudioServiceTrack(item, offset, id);
            else handlePlayTrack(item);
        } else {
            if (!isPlaying) audio.current.play() && setIsPlaying(true);
            else handlePauseTrack();
        }
    };

    const getPartNumbers = (playlist, key) => playlist.map((item) => item[key] !== null && item[key]);

    const getDistinctElements = (arr) => arr.filter((item, pos) => arr.indexOf(item) === pos);

    const getArePlaylistsComparable = () => {
        if (
            !loading &&
            !loadingAudioCohort &&
            audioCohortData?.audioCohort?.playList &&
            data?.audioEnginePlaylist?.playlist
        ) {
            return (
                JSON.stringify(
                    getDistinctElements(getPartNumbers(audioCohortData.audioCohort.playList || [], 'aePartNumber'))
                ) ===
                JSON.stringify(
                    getDistinctElements(getPartNumbers(data.audioEnginePlaylist.playlist || [], 'part_number'))
                )
            );
        }
        return false;
    };

    const arePlaylistsComparable = getArePlaylistsComparable();

    useEffect(() => {
        audioEnginePlaylist({
            variables: { contentId }
        });
        getAudioCohort({
            variables: { contentId: contentId }
        });
    }, [audioEnginePlaylist, contentId, getAudioCohort]);

    useEffect(() => {
        if (!loading && !loadingAudioCohort) {
            if (data?.audioEnginePlaylist?.playlist && document.getElementById('audio-aeplaylist-anchor')) {
                document.getElementById('audio-aeplaylist-anchor').classList.remove('hide');
            }
            if (audioCohortData?.audioCohort?.playList && document.getElementById('audio-asplaylist-anchor')) {
                document.getElementById('audio-asplaylist-anchor').classList.remove('hide');
            }
            if (audioCohortData?.audioCohort?.fileList && document.getElementById('audio-asfilelist-anchor')) {
                document.getElementById('audio-asfilelist-anchor').classList.remove('hide');
            }
            if (audioCohortData?.audioCohort?.sampleList?.length && document.getElementById('audio-assamplelist-anchor')) {
                document.getElementById('audio-assamplelist-anchor').classList.remove('hide');
            }
            if (sampleUrl && document.getElementById('audio-sample-anchor')) {
                document.getElementById('audio-sample-anchor').classList.remove('hide');
            }
            if (arePlaylistsComparable && document.getElementById('audio-compare-anchor')) {
                document.getElementById('audio-compare-anchor').classList.remove('hide');
            }
            return function cleanup() {
                document.getElementById('audio-aeplaylist-anchor').classList.add('hide');
                document.getElementById('audio-asplaylist-anchor').classList.add('hide');
                document.getElementById('audio-asfilelist-anchor').classList.add('hide');
                document.getElementById('audio-assamplelist-anchor').classList.add('hide');
                document.getElementById('audio-sample-anchor').classList.add('hide');
                document.getElementById('audio-compare-anchor').classList.add('hide');
            };
        }
    }, [data, audioCohortData, sampleUrl, arePlaylistsComparable, loading, loadingAudioCohort]);

    const AudioServiceVersion = audioCohortData?.audioCohort?.versionId && (
        <p>
            <strong>Version: </strong>
            <code>{audioCohortData?.audioCohort?.versionId}</code>
        </p>
    );

    return (
        <div>
            <audio
                className="audio-player mb-4"
                ref={audio}
                src={currentTrack}
                controls
                id="player"
                controlsList="nodownload"
            />
            {sampleUrl && (
                <section id="audio-sample-section" className="col-xs-12 col-lg-6 mb-12 anchor">
                    <h3 className="mb-4">Retail Sample</h3>
                    <PlayPauseButton
                        className="btn btn-link"
                        item={{ url: sampleUrl }}
                        text={isPlaying && currentTrack === sampleUrl ? 'Pause Sample' : 'Play Sample'}
                        isPlaying={currentTrack === sampleUrl && isPlaying}
                        handleClick={toggleTrack}
                    />
                </section>
            )}
            {!sampleUrl && (
                <section className="col-xs-12 col-lg-6 my-12">
                    <h3 className="my-4">Retail Sample</h3>
                    <p className="lead">No Retail Sample Available</p>
                </section>
            )}
            {/* If there is an audioServicePlaylist, and all parts audioServicePlaylist and audioEnginePlaylist are the same display this section.*/}{' '}
            {arePlaylistsComparable && (
                <section id="audio-compare-section" className="col-xs-12 col-lg-8 mb-12 anchor">
                    <h3 className="mb-4">Comparing Playlists</h3>
                    <Accordion collapsible className="mb-4">
                        <AccordionItem>
                            <AccordionButton className="btn pl-0">
                                <p className="mb-0">
                                    Comparing AudioEngine &amp; AudioService Playlists <FaChevronDown className="p-0" />
                                </p>
                            </AccordionButton>
                            <AccordionPanel className="flash">
                                <p className="mb-0">
                                    <small>
                                        Available only when titles are chaptered, and when the part/chapter numbering
                                        for both AudioEngine and AudioService are aligned, this view allows you to
                                        listen to and compare the AudioService (source) audio and AudioEngine
                                        (client-facing) audio.
                                    </small>
                                </p>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                    <section className="mb-12">
                        <h5>Front Matter</h5>
                        {(audioCohortData?.audioCohort.playList || [])
                            .filter((section) => {
                                return section.segments.filter((segment) => segment.aeChapterNumber !== null);
                            })
                            .filter((section) => section.sectionType === 'frontMatter')
                            .map((section, idx) => (
                                <div key={`frontMatter-${idx}`}>
                                    {section.aePartNumber !== null && (
                                        <h6 className="card card-sm card-header">
                                            asPart Number:{' '}
                                            {section.aePartNumber !== null ? section.aePartNumber : '[NULL]'}{' '}
                                            <span className="pl-0">
                                                asPart Name: {section.sectionName ? section.sectionName : '[NULL]'}
                                            </span>
                                        </h6>
                                    )}
                                    <CompareAEPlaylistAndAudioCohortSegments
                                        segments={section.segments.map((segment) => {
                                            return { ...segment, part: section.aePartNumber };
                                        })}
                                        aePlaylist={data?.audioEnginePlaylist?.playlist}
                                        onToggleTrack={toggleTrack}
                                        onASPlay={handlePlayAudioServiceTrack}
                                        onPause={handlePauseTrack}
                                        isAudioPlaying={isPlaying}
                                        selectedTrack={currentAudioServiceId}
                                        currentTrackUrl={currentTrack}
                                        fileList={audioCohortData?.audioCohort?.fileList || []}
                                    />
                                </div>
                            ))}
                    </section>
                    <section className="mb-12">
                        <h5>Body Matter</h5>
                        {(audioCohortData?.audioCohort.playList || [])
                            .filter((section) => {
                                return section.segments.filter((segment) => segment.aeChapterNumber !== null);
                            })
                            .filter((section) => section.sectionType === 'bodyMatter')
                            .map((section, idx) => (
                                <div key={`bodyMatter-${idx}`}>
                                    {section.aePartNumber !== null && (
                                        <h6 className="card card-sm card-header">
                                            asPart Number:{' '}
                                            {section.aePartNumber !== null ? section.aePartNumber : '[NULL]'}{' '}
                                            <span className="pl-0">
                                                asPart Name: {section.sectionName ? section.sectionName : '[NULL]'}
                                            </span>
                                        </h6>
                                    )}
                                    <CompareAEPlaylistAndAudioCohortSegments
                                        segments={section.segments.map((segment) => {
                                            return { ...segment, part: section.aePartNumber };
                                        })}
                                        aePlaylist={data?.audioEnginePlaylist?.playlist}
                                        onToggleTrack={toggleTrack}
                                        onASPlay={handlePlayAudioServiceTrack}
                                        onPause={handlePauseTrack}
                                        isAudioPlaying={isPlaying}
                                        selectedTrack={currentAudioServiceId}
                                        currentTrackUrl={currentTrack}
                                        fileList={audioCohortData?.audioCohort?.fileList || []}
                                    />
                                </div>
                            ))}
                    </section>
                    <section className="mb-12">
                        <h5>Back Matter</h5>
                        {(audioCohortData?.audioCohort.playList || [])
                            .filter((section) => {
                                return section.segments.filter((segment) => segment.aeChapterNumber !== null);
                            })
                            .filter((section) => section.sectionType === 'backMatter')
                            .map((section, idx) => (
                                <div key={`backMatter-${idx}`}>
                                    {section.aePartNumber !== null && (
                                        <h6 className="card card-sm card-header">
                                            asPart Number:{' '}
                                            {section.aePartNumber !== null ? section.aePartNumber : '[NULL]'}{' '}
                                            <span className="pl-0">
                                                asPart Name: {section.sectionName ? section.sectionName : '[NULL]'}
                                            </span>
                                        </h6>
                                    )}
                                    <CompareAEPlaylistAndAudioCohortSegments
                                        segments={section.segments.map((segment) => {
                                            return { ...segment, part: section.aePartNumber };
                                        })}
                                        aePlaylist={data?.audioEnginePlaylist?.playlist}
                                        onToggleTrack={toggleTrack}
                                        onASPlay={handlePlayAudioServiceTrack}
                                        onPause={handlePauseTrack}
                                        isAudioPlaying={isPlaying}
                                        selectedTrack={currentAudioServiceId}
                                        currentTrackUrl={currentTrack}
                                        fileList={audioCohortData?.audioCohort?.fileList || []}
                                    />
                                </div>
                            ))}
                    </section>
                </section>
            )}
            <section id="audio-aeplaylist-section" className="col-xs-12 col-lg-9 mb-12 anchor">
                <h3 className="mb-4">AudioEngine Playlist</h3>
                <Accordion collapsible className="mb-4">
                    <AccordionItem>
                        <AccordionButton className="btn pl-0">
                            <p className="mb-0">
                                What is the AudioEngine Playlist? <FaChevronDown className="p-0" />
                            </p>
                        </AccordionButton>
                        <AccordionPanel className="flash">
                            <p>
                                <small>
                                    <strong>AudioEngine Playlist</strong> contains the actual tracks and audio that an
                                    API partner would hear from AudioEngine (with any compression/encoding/etc already
                                    done). The part and chapter numbers are for playback order only, don't represent the
                                    actual book structure, and won't be shown to end listeners.
                                </small>
                            </p>
                            <p className="mb-0">
                                <small>
                                    The AudioEngine Playlist is available for all audiobooks where we've fully processed
                                    the audio and its available to our API/Integrated partners.
                                </small>
                            </p>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
                {loading && <Loading />}
                {!loading && !aePlaylistError && !data?.audioEnginePlaylist?.playlist.length && (
                    <p className="lead">No Playlist available.</p>
                )}
                {!loading && aePlaylistError && (
                    <p className="flash border-danger txt-danger">
                        <strong>Message: </strong>
                        <code>{aePlaylistError?.message}</code>
                    </p>
                )}
                {!loading && !aePlaylistError && (
                    <ul className="list-flat">
                        {(data?.audioEnginePlaylist?.playlist || []).map((item) => {
                            return (
                                <li
                                    key={`track_${item.part_number}_${item.chapter_number}`}
                                    className={classnames('px-2', {
                                        'bg-silver': currentTrack === item.url
                                    })}
                                >
                                    <PlayPauseButton
                                        className="btn btn-link"
                                        item={item}
                                        text={`Part ${item.part_number} - Chapter ${item.chapter_number}`}
                                        isPlaying={currentTrack === item.url && isPlaying}
                                        handleClick={toggleTrack}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
            <section className="col-xs-12 col-lg-8 pb-6">
                {loadingAudioCohort && (
                    <div className="p-4">
                        <h3 className="my-4">AudioService</h3>
                        <Loading />
                    </div>
                )}
                {!loadingAudioCohort && error && (
                    <>
                        <h3 className="my-4">AudioService</h3>
                        <p className="flash border-danger txt-danger">
                            <strong>Message:</strong>
                            <code>{error?.message}</code>
                        </p>
                    </>
                )}
                {audioCohortData && !loadingAudioCohort && !error && (
                    <>
                        <div className="mb-12">
                            <h3 id="audio-asfilelist-section" className="mt-6 anchor">
                                AudioService FileList
                            </h3>
                            {AudioServiceVersion}
                            <Accordion collapsible className="mb-4">
                                <AccordionItem>
                                    <AccordionButton className="btn pl-0">
                                        <p className="mb-0">
                                            What is the AudioService Filelist? <FaChevronDown className="p-0" />
                                        </p>
                                    </AccordionButton>
                                    <AccordionPanel className="flash">
                                        <p>
                                            <small>
                                                <strong>AudioService Filelist</strong> contains the source audio as
                                                provided by the publisher. The content of these files are the same as
                                                those you would listen to in the AudioEngine file list, but there may be
                                                some minor audible differences as these are uncompressed, unprocessed
                                                files. In addition, tracks may be merged or split between AudioService
                                                and AudioEngine, so not all tracks will pair/match perfectly between the
                                                two sources
                                            </small>
                                        </p>
                                        <p className="mb-0">
                                            <small>
                                                A small number of older titles are not in the AudioService. This does
                                                not matter for AudioEngine API partners if the title has an AudioEngine
                                                Playlist, but would prevent delivery to push partners, which use
                                                AudioService.
                                            </small>
                                        </p>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                            {audioCohortData?.audioCohort?.fileList && (
                                <>
                                    {/*If duplicate tracks, display message below.*/}
                                    {(audioCohortData?.audioCohort?.fileList || []).filter(
                                        (file) =>
                                            (audioCohortData?.audioCohort?.fileList || []).filter(
                                                (f) =>
                                                    file.checksum === f.checksum &&
                                                    f.fileName !== file.fileName &&
                                                    f.fileName
                                            ).length > 0 && file
                                    ).length > 0 && (
                                        <div className="flash border-navy txt-navy mt-6 mb-4">
                                            <ul className="row list-unstyled">
                                                <strong>Duplicate Tracks Detected:</strong>
                                                {(audioCohortData?.audioCohort?.fileList || []).map(
                                                    (file) =>
                                                        (audioCohortData?.audioCohort?.fileList || []).filter(
                                                            (f) =>
                                                                file.checksum === f.checksum &&
                                                                f.fileName !== file.fileName &&
                                                                f.fileName
                                                        ).length > 0 && (
                                                            <li key={file.fileName} className="txt-navy pl-2">
                                                                <small>{file.fileName}</small>
                                                            </li>
                                                        )
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                    <ul className="list-flat">
                                        {audioCohortData?.audioCohort?.fileList?.map((f) => {
                                            let duplicate = (audioCohortData?.audioCohort?.fileList || []).map(
                                                (file) =>
                                                    file.checksum === f.checksum &&
                                                    f.fileName !== file.fileName &&
                                                    `(Track is Duplicate of ${file.fileName})`
                                            );
                                            return (
                                                <li
                                                    key={`track_${f.checksum}_${f.fileName}`}
                                                    className={classnames('px-2', {
                                                        'bg-silver': currentTrack === f.url
                                                    })}
                                                >
                                                    <>
                                                        <PlayPauseButton
                                                            className="btn btn-link txt-left"
                                                            item={f}
                                                            text={f.fileName}
                                                            isPlaying={currentTrack === f.url && isPlaying}
                                                            handleClick={() => toggleTrack(f)}
                                                        />
                                                        {duplicate}
                                                    </>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                            )}
                            {!audioCohortData?.audioCohort?.fileList && !loadingAudioCohort && (
                                <p className="lead">No Filelist available.</p>
                            )}
                        </div>
                        <div className="mb-12">
                            <h3 id="audio-assamplelist-section" className="mb-4 mt-6 anchor">
                                AudioService SampleList
                            </h3>
                            {AudioServiceVersion}
                            <Accordion collapsible className="mb-4">
                                <AccordionItem>
                                    <AccordionButton className="btn pl-0">
                                        <p className="mb-0">
                                            What is the AudioService SampleList? <FaChevronDown className="p-0" />
                                        </p>
                                    </AccordionButton>
                                    <AccordionPanel className="flash">
                                        <p className="mb-0">
                                            <small>
                                                <strong>AudioService SampleList</strong> contains sample audio provided
                                                by the publisher, stored separately from the full audiobook source
                                                audio.
                                            </small>
                                        </p>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                            {!!audioCohortData?.audioCohort?.sampleList?.length && (
                                <ul className="list-flat">
                                    {audioCohortData.audioCohort.sampleList.map((sample) => (
                                        <li
                                            key={`sample_${sample.checksum}_${sample.fileName}`}
                                            className={classnames('px-2', {
                                                'bg-silver': currentTrack === sample.url
                                            })}
                                        >
                                            <PlayPauseButton
                                                className="btn btn-link txt-left"
                                                item={sample}
                                                text={sample.fileName}
                                                isPlaying={currentTrack === sample.url && isPlaying}
                                                handleClick={() => toggleTrack(sample)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {!audioCohortData?.audioCohort?.sampleList?.length && !loadingAudioCohort && (
                                <p className="lead">No SampleList available.</p>
                            )}
                        </div>
                        <div>
                            <h3 id="audio-asplaylist-section" className="mb-4 mt-6 anchor">
                                AudioService Playlist
                            </h3>
                            {AudioServiceVersion}
                            <Accordion collapsible className="mb-4">
                                <AccordionItem>
                                    <AccordionButton className="btn pl-0">
                                        <p className="mb-0">
                                            What is the AudioService Playlist? <FaChevronDown className="p-0" />
                                        </p>
                                    </AccordionButton>
                                    <AccordionPanel className="flash">
                                        <p>
                                            <small>
                                                <strong>AudioService Playlist</strong> is present only for Chaptered
                                                titles. The playlist presents the segment breaks and naming generated by
                                                the chaptering process (or if provided by the publisher).
                                            </small>
                                        </p>
                                        <p>
                                            <small>
                                                The track breaks created in chaptering should be reflected in the
                                                AudioEngine Playlist as well. A fully integrated v5 API partner may
                                                additionally present the human-readable part and chapter names from the
                                                playlist to users.
                                            </small>
                                        </p>
                                        <p>
                                            <small>
                                                The Part and Chapter numbers are for ordering only, and aren't what
                                                would be presented to listeners. [NULL] means that there is no value
                                                present, which is expected for Part names for many books.
                                            </small>
                                        </p>
                                        <p className="mb-0">
                                            <small>
                                                Apple is the only push partner that receives part/chapter names
                                            </small>
                                        </p>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Accordion>
                            {!audioCohortData?.audioCohort?.playList && !loadingAudioCohort && (
                                <p className="lead">No Playlist available.</p>
                            )}
                            {audioCohortData?.audioCohort?.playList && !loadingAudioCohort && (
                                <section id="audio-asplaylist-section" className="anchor">
                                    {(audioCohortData?.audioCohort.playList || [])
                                        .filter((section) => section.sectionType === 'frontMatter')
                                        .map((section, idx) => (
                                            <section className="mb-12" key={`frontMatter-${idx}`}>
                                                <h5>Front Matter</h5>
                                                <div>
                                                    {section.aePartNumber !== null && (
                                                        <h6 className="card card-sm card-header">
                                                            Part Number:{' '}
                                                            {section.aePartNumber !== null
                                                                ? section.aePartNumber
                                                                : '[NULL]'}{' '}
                                                            <span className="pl-0">
                                                                Part Name: {section.sectionName || '[NULL]'}
                                                            </span>
                                                        </h6>
                                                    )}
                                                    <AudioCohortSegments
                                                        segments={section.segments.map((segment) => {
                                                            return { ...segment, part: section.aePartNumber };
                                                        })}
                                                        onASPlay={handlePlayAudioServiceTrack}
                                                        onPause={handlePauseTrack}
                                                        isAudioPlaying={isPlaying}
                                                        selectedTrack={currentAudioServiceId}
                                                        fileList={audioCohortData?.audioCohort?.fileList || []}
                                                    />
                                                </div>
                                            </section>
                                        ))}
                                    {(audioCohortData?.audioCohort.playList || [])
                                        .filter((section) => section.sectionType === 'bodyMatter')
                                        .map((section, idx) => (
                                            <section className="mb-12" key={`bodymatter-${idx}`}>
                                                <h5>Body Matter</h5>
                                                <div key={`bodyMatter-${idx}`}>
                                                    {section.aePartNumber !== null && (
                                                        <h6 className="card card-sm card-header">
                                                            Part Number:{' '}
                                                            {section.aePartNumber !== null
                                                                ? section.aePartNumber
                                                                : '[NULL]'}{' '}
                                                            <span className="pl-0">
                                                                Part Name: {section.sectionName || '[NULL]'}
                                                            </span>
                                                        </h6>
                                                    )}
                                                    <AudioCohortSegments
                                                        segments={section.segments.map((segment) => {
                                                            return { ...segment, part: section.aePartNumber };
                                                        })}
                                                        onASPlay={handlePlayAudioServiceTrack}
                                                        onPause={handlePauseTrack}
                                                        isAudioPlaying={isPlaying}
                                                        selectedTrack={currentAudioServiceId}
                                                        fileList={audioCohortData?.audioCohort?.fileList || []}
                                                    />
                                                </div>
                                            </section>
                                        ))}
                                    {(audioCohortData?.audioCohort?.playList || [])
                                        .filter((section) => section.sectionType === 'backMatter')
                                        .map((section, idx) => (
                                            <section className="mb-12" key={`backmatter-${idx}`}>
                                                <h5>Back Matter</h5>
                                                <div key={`backMatter-${idx}`}>
                                                    {section.aePartNumber !== null && (
                                                        <h6 className="card card-sm card-header">
                                                            Part Number:{' '}
                                                            {section.aePartNumber !== null
                                                                ? section.aePartNumber
                                                                : '[NULL]'}{' '}
                                                            <span className="pl-0">
                                                                Part Name: {section.sectionName || '[NULL]'}
                                                            </span>
                                                        </h6>
                                                    )}
                                                    <AudioCohortSegments
                                                        segments={section.segments.map((segment) => {
                                                            return { ...segment, part: section.aePartNumber };
                                                        })}
                                                        onASPlay={handlePlayAudioServiceTrack}
                                                        onPause={handlePauseTrack}
                                                        isAudioPlaying={isPlaying}
                                                        selectedTrack={currentAudioServiceId}
                                                        fileList={audioCohortData?.audioCohort?.fileList || []}
                                                    />
                                                </div>
                                            </section>
                                        ))}
                                </section>
                            )}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
};
function AudioCohortSegments({
    segments,
    onASPlay = () => {},
    onPause = () => {},
    isAudioPlaying = false,
    fileList = [],
    selectedTrack = null
}) {
    return (
        <>
            {segments.map((segment, idx) => {
                let list = getChecksumAndUrl(segment.start.checksum, segment.end.checksum, fileList) || [];

                return (
                    <Accordion
                        collapsible
                        key={`${segment.aeChapterNumber}-${segment.name}`}
                        className="card card-sm mb-4 ml-6 col-lg-12"
                    >
                        <AccordionItem className="accordionItem">
                            <AccordionButton className="btn txt-left card-header">
                                <h6 className="btn txt-left pl-1 col-xs-12">
                                    {segment.aeChapterNumber !== null ? (
                                        <span className="txt-label mb-0">
                                            <strong>Chapter Number: </strong>
                                            {segment.aeChapterNumber}
                                        </span>
                                    ) : (
                                        <span className="txt-label mb-0">
                                            <strong>Chapter Number: </strong>[NULL]
                                        </span>
                                    )}
                                </h6>
                                <br />
                                <h6 className="btn txt-left pl-1">
                                    {segment.name ? (
                                        <span className="txt-label mb-0">
                                            <strong>Chapter Name: </strong>
                                            {segment.name}
                                            <FaAngleDown className="ml-1" />
                                        </span>
                                    ) : (
                                        <span className="txt-label mb-0">
                                            <strong>Chapter Name: </strong>[NULL]
                                            <FaAngleDown className="ml-1" />
                                        </span>
                                    )}
                                </h6>
                            </AccordionButton>
                            <AccordionPanel className="card-body p-3 bg-light">
                                <div>
                                    <p className="pl-xl-2">
                                        <strong>AudioService Details:</strong>
                                    </p>
                                    <div className="row middle-xs col-xl-10 mx-lg-auto">
                                        {list.length === 1 && (
                                            <div key={`Part${segment.part}-${segment.start.checksum}-${idx}`}>
                                                <span className="txt-label col-xs-12">
                                                    Start:{' '}
                                                    <span className="txt-400">
                                                        {convertToDuration(segment.start.offset)}
                                                    </span>
                                                </span>
                                                <span className="txt-label col-xs-12">
                                                    <code className="txt-400">{segment.start.checksum}</code>
                                                </span>
                                                <div className="txt-center col-xs-7 col-lg-6">
                                                    <FaArrowRight className="panel-svg" />
                                                </div>
                                                <span className="txt-label col-xs-12">
                                                    End:{' '}
                                                    <span className="txt-400">
                                                        {convertToDuration(segment.end.offset)}
                                                    </span>
                                                </span>
                                                <span className="txt-label col-xs-12">
                                                    <code className="txt-400">{segment.end.checksum}</code>
                                                </span>
                                                <span className="py-2 col-xs-12">
                                                    {(!isAudioPlaying ||
                                                        (isAudioPlaying &&
                                                            selectedTrack !==
                                                                `Part${segment.part}-${segment.start.checksum}-${idx}`)) && (
                                                        <button
                                                            className="btn btn-link btn-sm pl-0 mt-2"
                                                            onClick={() =>
                                                                onASPlay(
                                                                    list[0],
                                                                    {
                                                                        start: segment.start.offset,
                                                                        end: segment.end.offset
                                                                    },
                                                                    `Part${segment.part}-${segment.start.checksum}-${idx}`
                                                                )
                                                            }
                                                        >
                                                            Play AudioService Track{' '}
                                                            <FaPlay className="ml-2 pl-1 panel-svg" />
                                                        </button>
                                                    )}
                                                    {isAudioPlaying &&
                                                        selectedTrack ===
                                                            `Part${segment.part}-${segment.start.checksum}-${idx}` && (
                                                            <button
                                                                className="btn btn-link btn-sm ml-0 bg-silver mt-2"
                                                                onClick={onPause}
                                                            >
                                                                Pause AudioService Track{' '}
                                                                <FaPause className="ml-2 pl-1 panel-svg" />
                                                            </button>
                                                        )}
                                                </span>
                                            </div>
                                        )}
                                        {list.length > 1 && (
                                            <div>
                                                {list.map((item, idx) => {
                                                    if (idx === 0) {
                                                        return (
                                                            <div
                                                                key={`Part${segment.part}-${segment.start.checksum}-${idx}`}
                                                            >
                                                                <span className="txt-label col-xs-12">
                                                                    Start:{' '}
                                                                    <span className="txt-400">
                                                                        {convertToDuration(segment.start.offset)}
                                                                    </span>
                                                                </span>
                                                                <span className="txt-label col-xs-12">
                                                                    <code className="txt-400 py-2">
                                                                        {segment.start.checksum}
                                                                    </code>
                                                                </span>
                                                                {(!isAudioPlaying ||
                                                                    (isAudioPlaying &&
                                                                        selectedTrack !==
                                                                            `Part${segment.part}-${segment.start.checksum}-${idx}`)) && (
                                                                    <button
                                                                        className="btn btn-link btn-sm pl-2 mt-2"
                                                                        onClick={() =>
                                                                            onASPlay(
                                                                                item,
                                                                                {
                                                                                    start: segment.start.offset,
                                                                                    end: null
                                                                                },
                                                                                `Part${segment.part}-${segment.start.checksum}-${idx}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Play AudioService Track{' '}
                                                                        <FaPlay className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}
                                                                {isAudioPlaying &&
                                                                    selectedTrack ===
                                                                        `Part${segment.part}-${segment.start.checksum}-${idx}` && (
                                                                        <button
                                                                            className="btn btn-link btn-sm ml-2 bg-silver mt-2"
                                                                            onClick={onPause}
                                                                        >
                                                                            Pause AudioService Track{' '}
                                                                            <FaPause className="ml-2 pl-1 panel-svg" />
                                                                        </button>
                                                                    )}{' '}
                                                                <div className="txt-center col-xs-7 col-lg-6 py-2">
                                                                    <FaArrowRight className="panel-svg" />
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    if (idx !== 0 && idx !== list.length - 1) {
                                                        return (
                                                            <div key={`Part${segment.part}-${item}-${idx}`}>
                                                                <span className="txt-label col-xs-12">
                                                                    <code className="txt-400 py-2">
                                                                        {item?.checksum}
                                                                    </code>
                                                                </span>
                                                                {(!isAudioPlaying ||
                                                                    (isAudioPlaying &&
                                                                        selectedTrack !==
                                                                            `Part${segment.part}-${item?.checksum}-${idx}`)) && (
                                                                    <button
                                                                        className="btn btn-link btn-sm pl-2 mt-2"
                                                                        onClick={() =>
                                                                            onASPlay(
                                                                                item,
                                                                                {
                                                                                    start: 0,
                                                                                    end: null
                                                                                },
                                                                                `Part${segment.part}-${item?.checksum}-${idx}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Play AudioService Track{' '}
                                                                        <FaPlay className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}{' '}
                                                                {isAudioPlaying &&
                                                                    selectedTrack ===
                                                                        `Part${segment.part}-${item?.checksum}-${idx}` && (
                                                                        <button
                                                                            className="btn btn-link btn-sm ml-2 bg-silver mt-2"
                                                                            onClick={onPause}
                                                                        >
                                                                            Pause AudioService Track{' '}
                                                                            <FaPause className="ml-2 pl-1 panel-svg" />
                                                                        </button>
                                                                    )}
                                                                <div className="txt-center col-xs-7 col-lg-6 py-2">
                                                                    <FaArrowRight className="panel-svg" />
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    if (idx === list.length - 1) {
                                                        return (
                                                            <div
                                                                key={`Part${segment.part}-${segment.end.checksum}-${idx}`}
                                                            >
                                                                <span className="txt-label col-xs-12">
                                                                    End:{' '}
                                                                    <span className="txt-400">
                                                                        {convertToDuration(segment.end.offset)}
                                                                    </span>
                                                                </span>
                                                                <span className="txt-label col-xs-12">
                                                                    <code className="txt-400 py-2">
                                                                        {segment.end.checksum}
                                                                    </code>
                                                                </span>
                                                                {(!isAudioPlaying ||
                                                                    (isAudioPlaying &&
                                                                        selectedTrack !==
                                                                            `Part${segment.part}-${segment.end.checksum}-${idx}`)) && (
                                                                    <button
                                                                        className="btn btn-link btn-sm pl-2 mt-2"
                                                                        onClick={() =>
                                                                            onASPlay(
                                                                                item,
                                                                                {
                                                                                    start: 0,
                                                                                    end: segment.end.offset
                                                                                },
                                                                                `Part${segment.part}-${segment.end.checksum}-${idx}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Play AudioService Track{' '}
                                                                        <FaPlay className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}
                                                                {isAudioPlaying &&
                                                                    selectedTrack ===
                                                                        `Part${segment.part}-${segment.end.checksum}-${idx}` && (
                                                                        <button
                                                                            className="btn btn-link btn-sm ml-2 bg-silver mt-2"
                                                                            onClick={onPause}
                                                                        >
                                                                            Pause AudioService Track{' '}
                                                                            <FaPause className="ml-2 pl-1 panel-svg" />
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        );
                                                    } else {
                                                        return null;
                                                    }
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                );
            })}
        </>
    );
}

function CompareAEPlaylistAndAudioCohortSegments({
    segments,
    aePlaylist = [],
    onToggleTrack = () => {},
    onASPlay = () => {},
    onPause = () => {},
    isAudioPlaying = false,
    fileList = [],
    selectedTrack = null,
    currentTrackUrl = null
}) {
    return (
        <>
            {segments.map((segment, idx) => {
                let list = getChecksumAndUrl(segment.start.checksum, segment.end.checksum, fileList) || [];
                return (
                    <Accordion
                        collapsible
                        key={`${segment.aeChapterNumber}-${segment.name}`}
                        className="card card-sm mb-4 ml-6 col-lg-12"
                    >
                        <AccordionItem className="accordionItem">
                            <AccordionButton className="btn txt-left card-header">
                                <h6 className="btn txt-left pl-1 col-xs-12">
                                    {segment.aeChapterNumber !== null ? (
                                        <span className="txt-label mb-0">
                                            <strong>asChapter Number: </strong>
                                            {segment.aeChapterNumber}
                                        </span>
                                    ) : (
                                        <span className="txt-label mb-0">
                                            <strong>asChapter Number: </strong>[NULL]
                                        </span>
                                    )}
                                </h6>
                                <br />
                                <h6 className="btn txt-left pl-1">
                                    {segment.name ? (
                                        <span className="txt-label mb-0">
                                            <strong>asChapter Name: </strong>
                                            {segment.name}
                                            <FaAngleDown className="ml-1" />
                                        </span>
                                    ) : (
                                        <span className="txt-label mb-0">
                                            <strong>asChapter Name: </strong>[NULL]
                                            <FaAngleDown className="ml-1" />
                                        </span>
                                    )}
                                </h6>
                            </AccordionButton>
                            <AccordionPanel className="card-body p-3 bg-light">
                                <div className="mb-3">
                                    <p className="pl-xl-2">
                                        <strong>AudioService Playlist Details:</strong>
                                    </p>
                                    <div className="row middle-xs">
                                        {list.length === 1 && (
                                            <div
                                                className="col-xs-12 col-lg-10 mx-lg-auto"
                                                key={`Part${segment.part}-${segment.start.checksum}-${idx}`}
                                            >
                                                <span className="txt-label col-xs-12">
                                                    Start:{' '}
                                                    <span className="txt-400">
                                                        {convertToDuration(segment.start.offset)}
                                                    </span>
                                                </span>
                                                <span className="txt-label col-xs-12">
                                                    <code className="txt-400">{segment.start.checksum}</code>
                                                </span>
                                                <div className="txt-center col-xs-7 col-lg-6">
                                                    <FaArrowRight className="panel-svg" />
                                                </div>
                                                <span className="txt-label col-xs-12">
                                                    End:{' '}
                                                    <span className="txt-400">
                                                        {convertToDuration(segment.end.offset)}
                                                    </span>
                                                </span>
                                                <span className="txt-label col-xs-12">
                                                    <code className="txt-400">{segment.end.checksum}</code>
                                                </span>
                                                <span className="py-2 col-xs-12">
                                                    {(!isAudioPlaying ||
                                                        (isAudioPlaying &&
                                                            selectedTrack !==
                                                                `Part${segment.part}-${segment.start.checksum}-${idx}`)) &&
                                                        list[0]?.url && (
                                                            <button
                                                                className="btn btn-link btn-sm pl-0 mt-2"
                                                                onClick={() =>
                                                                    onASPlay(
                                                                        list[0],
                                                                        {
                                                                            start: segment.start.offset,
                                                                            end: segment.end.offset
                                                                        },
                                                                        `Part${segment.part}-${segment.start.checksum}-${idx}`
                                                                    )
                                                                }
                                                            >
                                                                Play AudioService Track{' '}
                                                                <FaPlay className="ml-2 pl-1 panel-svg" />
                                                            </button>
                                                        )}
                                                    {isAudioPlaying &&
                                                        selectedTrack ===
                                                            `Part${segment.part}-${segment.start.checksum}-${idx}` && (
                                                            <button
                                                                className="btn btn-link btn-sm ml-0 bg-silver mt-2"
                                                                onClick={onPause}
                                                            >
                                                                Pause AudioService Track{' '}
                                                                <FaPause className="ml-2 pl-1 panel-svg" />
                                                            </button>
                                                        )}
                                                </span>
                                            </div>
                                        )}
                                        {list.length > 1 &&
                                            list.map((item, idx) => {
                                                if (idx === 0) {
                                                    return (
                                                        <div
                                                            className="col-xs-12 col-lg-10 mx-lg-auto"
                                                            key={`Part${segment.part}-${segment.start.checksum}-${idx}`}
                                                        >
                                                            <span className="txt-label col-xs-12">
                                                                Start:{' '}
                                                                <span className="txt-400">
                                                                    {convertToDuration(segment.start.offset)}
                                                                </span>
                                                            </span>
                                                            <span className="txt-label col-xs-12">
                                                                <code className="txt-400 py-2">
                                                                    {segment.start.checksum}
                                                                </code>
                                                            </span>
                                                            {(!isAudioPlaying ||
                                                                (isAudioPlaying &&
                                                                    selectedTrack !==
                                                                        `Part${segment.part}-${segment.start.checksum}-${idx}`)) &&
                                                                item?.url && (
                                                                    <button
                                                                        className="btn btn-link btn-sm pl-2 mt-2"
                                                                        onClick={() =>
                                                                            onASPlay(
                                                                                item,
                                                                                {
                                                                                    start: segment.start.offset,
                                                                                    end: null
                                                                                },
                                                                                `Part${segment.part}-${segment.start.checksum}-${idx}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Play AudioService Track{' '}
                                                                        <FaPlay className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}
                                                            {isAudioPlaying &&
                                                                selectedTrack ===
                                                                    `Part${segment.part}-${segment.start.checksum}-${idx}` && (
                                                                    <button
                                                                        className="btn btn-link btn-sm ml-2 bg-silver mt-2"
                                                                        onClick={onPause}
                                                                    >
                                                                        Pause AudioService Track{' '}
                                                                        <FaPause className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}{' '}
                                                            <div className="txt-center col-xs-7 col-lg-6 py-2">
                                                                <FaArrowRight className="panel-svg" />
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (idx !== 0 && idx !== list.length - 1) {
                                                    return (
                                                        <div
                                                            className="col-xs-12 col-lg-10 mx-lg-auto"
                                                            key={`Part${segment.part}-${item?.checksum}-${idx}`}
                                                        >
                                                            <span className="txt-label col-xs-12">
                                                                <code className="txt-400 py-2">{item?.checksum}</code>
                                                            </span>
                                                            {(!isAudioPlaying ||
                                                                (isAudioPlaying &&
                                                                    selectedTrack !==
                                                                        `Part${segment.part}-${item?.checksum}-${idx}`)) &&
                                                                item?.url && (
                                                                    <button
                                                                        className="btn btn-link btn-sm pl-2 mt-2"
                                                                        onClick={() =>
                                                                            onASPlay(
                                                                                item,
                                                                                {
                                                                                    start: 0,
                                                                                    end: null
                                                                                },
                                                                                `Part${segment.part}-${item?.checksum}-${idx}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Play AudioService Track{' '}
                                                                        <FaPlay className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}{' '}
                                                            {isAudioPlaying &&
                                                                selectedTrack ===
                                                                    `Part${segment.part}-${item?.checksum}-${idx}` && (
                                                                    <button
                                                                        className="btn btn-link btn-sm ml-2 bg-silver mt-2"
                                                                        onClick={onPause}
                                                                    >
                                                                        Pause AudioService Track{' '}
                                                                        <FaPause className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}
                                                            <div className="txt-center col-xs-7 col-lg-6 py-2">
                                                                <FaArrowRight className="panel-svg" />
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (idx === list.length - 1) {
                                                    return (
                                                        <div
                                                            className="col-xs-12 col-lg-10 mx-lg-auto"
                                                            key={`Part${segment.part}-${segment.end.checksum}-${idx}`}
                                                        >
                                                            <span className="txt-label col-xs-12">
                                                                End:{' '}
                                                                <span className="txt-400">
                                                                    {convertToDuration(segment.end.offset)}
                                                                </span>
                                                            </span>
                                                            <span className="txt-label col-xs-12">
                                                                <code className="txt-400 py-2">
                                                                    {segment.end.checksum}
                                                                </code>
                                                            </span>
                                                            {(!isAudioPlaying ||
                                                                (isAudioPlaying &&
                                                                    selectedTrack !==
                                                                        `Part${segment.part}-${segment.end.checksum}-${idx}`)) &&
                                                                item?.url && (
                                                                    <button
                                                                        className="btn btn-link btn-sm pl-2 mt-2"
                                                                        onClick={() =>
                                                                            onASPlay(
                                                                                item,
                                                                                {
                                                                                    start: 0,
                                                                                    end: segment.end.offset
                                                                                },
                                                                                `Part${segment.part}-${segment.end.checksum}-${idx}`
                                                                            )
                                                                        }
                                                                    >
                                                                        Play AudioService Track{' '}
                                                                        <FaPlay className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}
                                                            {isAudioPlaying &&
                                                                selectedTrack ===
                                                                    `Part${segment.part}-${segment.end.checksum}-${idx}` && (
                                                                    <button
                                                                        className="btn btn-link btn-sm ml-2 bg-silver mt-2"
                                                                        onClick={onPause}
                                                                    >
                                                                        Pause AudioService Track{' '}
                                                                        <FaPause className="ml-2 pl-1 panel-svg" />
                                                                    </button>
                                                                )}
                                                        </div>
                                                    );
                                                } else {
                                                    return null;
                                                }
                                            })}
                                    </div>
                                </div>
                                <hr className="bg-silver" />
                                <div className="mt-3">
                                    <p className="pl-xl-2 col-xs-12">
                                        <strong>AudioEngine Playlist Details:</strong>
                                    </p>
                                    <div className="row middle-xs col-xs-12 col-lg-10 mx-lg-auto">
                                        {(aePlaylist || [])
                                            .filter(
                                                (item) =>
                                                    item.chapter_number === segment.aeChapterNumber &&
                                                    item.part_number === segment.part
                                            )
                                            .map((item) => (
                                                <div key={`track_${item.part_number}_${item.chapter_number}`}>
                                                    <span className="txt-label col-xs-12 mb-2">
                                                        aePart Number:{' '}
                                                        <span className="txt-400">{item.part_number}</span>
                                                    </span>
                                                    <span className="txt-label col-xs-12">
                                                        aeChapter Number:{' '}
                                                        <span className="txt-400">{item.chapter_number}</span>
                                                    </span>
                                                    <PlayPauseButton
                                                        className={
                                                            isAudioPlaying && currentTrackUrl === item.url
                                                                ? 'btn btn-link btn-sm pl-2 mt-2 bg-silver'
                                                                : 'btn btn-link btn-sm pl-2 mt-2'
                                                        }
                                                        item={item}
                                                        text={
                                                            isAudioPlaying && currentTrackUrl === item.url
                                                                ? 'Pause AudioEngine Track'
                                                                : 'Play AudioEngine Track'
                                                        }
                                                        isPlaying={currentTrackUrl === item.url && isAudioPlaying}
                                                        handleClick={() => onToggleTrack(item)}
                                                    />
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                );
            })}
        </>
    );
}

export default Audio;
